import {
  detachLayoutProps,
  WSElement,
  WSFlexBox,
  WSMarginProps,
  WSPanel,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { classNames } from "primereact/utils";
import React from "react";
import styles from "./index.module.scss";
import { StepPoint } from "../Layout/StepPoint";
import { ModalFlowStep } from "./types";

interface ModalNavigationProps<P extends object> extends WSMarginProps {
  steps: ModalFlowStep<P>[];
  currentStep: ModalFlowStep<P>;
  currentSubStep?: ModalFlowStep<P>;
  defaultStatuses?: boolean;
}
export function ModalNavigation<P extends object>(
  props: ModalNavigationProps<P>
) {
  const { steps, currentStep, currentSubStep, defaultStatuses, ...otherProps } =
    props;

  const { layoutProps } = detachLayoutProps(otherProps);

  const activeIndex =
    steps.findIndex(step => step.key === currentStep.key) || 0;

  return (
    <WSElement className={styles.navigation} {...layoutProps}>
      {props.steps.map((step, index) => {
        const isActive = index === activeIndex;
        const isPreviousStep = index < activeIndex;
        const isNextStep = index > activeIndex;
        const isComplete =
          isPreviousStep && defaultStatuses ? true : step.status === "Complete";
        const isPending =
          !isComplete &&
          (isNextStep && defaultStatuses ? true : step.status === "Pending");
        const isLoading =
          !isComplete && !isPending && step.status === "Loading";

        return (
          <WSPanel
            key={step.title}
            className={classNames(
              styles.navigationItem,
              isActive && styles.navigationItemActive
            )}
          >
            <WSFlexBox justify="space-between" wrap="nowrap">
              <WSText weight="medium" color={isActive ? "gray600" : "gray400"}>
                {step.title}
              </WSText>
              {isComplete && <WSPill icon theme="success" />}
              {isPending && <WSPill icon="time" theme="neutral" />}
              {isLoading && (
                <WSPill shimmer={true} icon="loader" theme="blue" />
              )}
            </WSFlexBox>

            {isActive && !isComplete && !isPending && step.steps && (
              <NavigationLevel2
                currentStep={currentStep}
                currentSubStep={currentSubStep}
                isLoading={isLoading}
                steps={step.steps}
              />
            )}
          </WSPanel>
        );
      })}
    </WSElement>
  );
}

interface NavigationChildProps<
  P extends object
> extends ModalNavigationProps<P> {
  isLoading?: boolean;
}

export function NavigationLevel2<P extends object>(
  props: NavigationChildProps<P>
) {
  const { steps, isLoading, currentSubStep } = props;

  const activeIndex = steps.findIndex(step => currentSubStep?.key === step.key);

  return (
    <WSElement className={styles.navigationLevel2}>
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const isComplete = index < activeIndex;

        return (
          <WSElement
            key={step.key}
            className={classNames(
              styles.navigationItem,
              isActive && styles.navigaitonItemActive,
              isComplete && styles.navigationItemComplete
            )}
          >
            <StepPoint
              isLoading={isLoading}
              isActive={isActive}
              isComplete={isComplete}
            />

            <WSText.ParagraphXs
              weight="medium"
              color={isActive || isComplete ? "gray600" : "gray300"}
            >
              {step.title}
            </WSText.ParagraphXs>
          </WSElement>
        );
      })}
    </WSElement>
  );
}
